import { useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingEmbed } from "@/components/site/BookingEmbed";
import {
  CompanyPageHero,
  CompanyPageShell,
  CompanyServiceRow,
  useCompanyLang
} from "@/components/site/company/CompanyShared";
import {
  companyBookingHref,
  companyCatalogGroups,
  companyDirectBookingModes,
  companyDirectServices,
  companyInquiryHref,
  companyIsDualBooking,
  companyServiceLabel,
  isCompanyDirectBooking
} from "@/data/company";
import { getBookingAction, type BookingMode } from "@/data/certificateBookingActions";
import { certificates } from "@/data/certificates";
import { PageMeta } from "@/components/site/PageMeta";

const parseBookingMode = (value: string | null): BookingMode | null =>
  value === "digital" || value === "onsite" ? value : null;

const ForetagBokaTid = () => {
  const { lang, copy } = useCompanyLang();
  const b = copy.bokaTid;
  const [searchParams] = useSearchParams();
  const intyg = searchParams.get("intyg") ?? undefined;
  const besok = parseBookingMode(searchParams.get("besok"));

  const knownSlugs = useMemo(() => new Set(certificates.map((cert) => cert.slug)), []);



  if (intyg && !isCompanyDirectBooking(intyg)) {
    return <Navigate to={companyInquiryHref(lang, knownSlugs.has(intyg) ? intyg : undefined)} replace />;
  }

  if (intyg && isCompanyDirectBooking(intyg)) {
    const action = getBookingAction(intyg);
    const title = companyServiceLabel(intyg, lang);
    const modes = companyDirectBookingModes(intyg);
    const initialMode = besok && modes.includes(besok) ? besok : null;

    return (
      <CompanyPageShell breadcrumbLabel={copy.nav.bokaTid}>
      <PageMeta title={b.metaTitle} description={b.metaDescription} titleMaxLength={70} />
        <CompanyPageHero label={b.label} title={`${b.bookingTitle} ${title}`}>
          <p>{b.bookingHelp}</p>
          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap">
            <Button asChild variant="ghost-outline" size="lg">
              <a href={companyBookingHref(lang)}>{b.backToList}</a>
            </Button>
            <Button asChild variant="ghost-outline" size="lg">
              <a href={companyInquiryHref(lang, intyg)}>{b.inquiryLink}</a>
            </Button>
          </div>
        </CompanyPageHero>

        <section className="pb-20">
          <div className="container-wide">
            <BookingEmbed
              key={`${intyg}-${initialMode ?? "choose"}`}
              action={action}
              title={title}
              slug={intyg}
              initialMode={initialMode}
              iframeClassName="h-[1100px] w-full border-0 sm:h-[1000px]"
            />
          </div>
        </section>
      </CompanyPageShell>
    );
  }

  const directServices = companyDirectServices();

  return (
    <CompanyPageShell breadcrumbLabel={copy.nav.bokaTid}>
      <CompanyPageHero label={b.label} title={b.title}>
        <p>{b.subtitle}</p>
        <a
          href={companyInquiryHref(lang)}
          className="inline-flex items-center text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          {b.inquiryLink}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </a>
      </CompanyPageHero>

      <section className="pb-20">
        <div className="container-wide space-y-12">
          {companyCatalogGroups.map((group) => {
            const choices = directServices.filter((service) => group.match(service));
            if (!choices.length) return null;
            return (
              <div key={group.id}>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                  {group.title[lang]}
                </h2>
                <div className="mt-5 space-y-3">
                  {choices.map((service) => (
                    <CompanyServiceRow
                      key={service.slug}
                      title={service.title[lang]}
                      intro={[
                        service.price[lang],
                        companyIsDualBooking(service.slug)
                          ? lang === "sv" ? "Digitalt eller på plats" : "Digital or on-site"
                          : null,
                      ].filter(Boolean).join(" · ")}
                      actions={
                        <Button asChild variant="default" size="sm">
                          <a href={companyBookingHref(lang, service.slug)}>
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {b.bookVisit}
                          </a>
                        </Button>
                      }
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </CompanyPageShell>
  );
};

export default ForetagBokaTid;
